import type { CourseDefinition } from '../types/course';
import { typeset } from './optional-libraries';

export async function renderCourse(app: HTMLElement, course: CourseDefinition): Promise<void> {
  app.innerHTML = `
    <div class="app course-app course-${course.moduleId} track-${course.track}" data-track="${course.track}" data-feedback-mode="${course.feedbackMode}">
      <header class="topbar">
        <a class="navbtn topbar-link course-back" href="/${course.subjectId}" data-nav>← 返回</a>
        <a class="brand brand-button" href="/${course.subjectId}" data-nav>${course.subject}课程</a>
        <div class="chapter" id="chapterLabel">导入</div>
        <div class="progress" aria-label="课程进度"><span id="progressBar"></span></div>
        <a class="navbtn topbar-link exit-course" href="/" data-nav>退出课程</a>
        <button class="navbtn" id="tocBtn">目录</button>
      </header>
      <main class="stage" id="stage">
        ${course.slides.map((slide, index) => `
          <section class="slide${index === 0 ? ' active' : ''}"
            data-chapter="${slide.chapter}" ${slide.toc ? `data-toc="${slide.toc}"` : ''}
            ${slide.part ? `data-part="${slide.part}"` : ''}>
            ${index > 0 ? '<button class="slide-back" type="button" data-back>← 返回上一页</button>' : ''}
            ${slide.html}</section>
        `).join('')}
      </main>
      <footer class="bottombar">
        <div class="navgroup"><button class="navbtn" id="prev">← 上一页</button><button class="navbtn" id="next">下一页 →</button></div>
        <div class="navgroup"><button class="navbtn primary" id="quickBtn">${course.quickReferenceLabel}</button><button class="navbtn hide-mobile" id="fullBtn">全屏</button><div class="page" id="pageNo">1 / 1</div></div>
      </footer>
    </div>
    <div class="toc" id="toc" role="dialog" aria-modal="true" aria-labelledby="tocTitle">
      <div class="tocbox"><h2 id="tocTitle">课程目录</h2><div class="tocgrid" id="tocGrid"></div><button class="close" id="closeToc">返回课程</button></div>
    </div>`;

  const slides = Array.from(app.querySelectorAll<HTMLElement>('.slide'));
  const stage = app.querySelector<HTMLElement>('#stage');
  const toc = app.querySelector<HTMLElement>('#toc');
  if (!stage || !toc) throw new Error('课程模板缺少舞台或目录');
  let current = 0;
  let lastFocused: HTMLElement | null = null;

  if (course.feedbackMode === 'visible') {
    app.querySelectorAll<HTMLElement>('[data-kind="example"], [data-kind="practice"]').forEach((card) => {
      if (card.hasAttribute('data-manual-reveal')) return;
      const answerPanel = card.querySelector<HTMLElement>('[data-reveal-panel="answer"]');
      const solutionPanel = card.querySelector<HTMLElement>('[data-reveal-panel="solution"]');
      answerPanel?.classList.add('show');
      solutionPanel?.classList.add('show');
      card.querySelector<HTMLElement>('.actions')?.remove();
    });
  }

  const show = async (index: number, focusTitle = false) => {
    current = Math.max(0, Math.min(slides.length - 1, index));
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === current;
      slide.classList.toggle('active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
    const active = slides[current];
    if (!active) return;
    app.querySelector<HTMLElement>('#pageNo')!.textContent = `${current + 1} / ${slides.length}`;
    app.querySelector<HTMLElement>('#chapterLabel')!.textContent = active.dataset.chapter ?? '';
    const progress = app.querySelector<HTMLElement>('.progress')!;
    progress.setAttribute('role', 'progressbar');
    progress.setAttribute('aria-valuemin', '1');
    progress.setAttribute('aria-valuemax', String(slides.length));
    progress.setAttribute('aria-valuenow', String(current + 1));
    app.querySelector<HTMLElement>('#progressBar')!.style.width = `${Math.round((current + 1) * 100 / slides.length)}%`;
    (app.querySelector<HTMLButtonElement>('#prev')!).disabled = current === 0;
    (app.querySelector<HTMLButtonElement>('#next')!).disabled = current === slides.length - 1;
    active.scrollTop = 0;
    await typeset(active);
    if (focusTitle) active.querySelector<HTMLElement>('h1, h2')?.focus({ preventScroll: true });
  };

  const closeToc = () => {
    toc.classList.remove('open');
    lastFocused?.focus();
  };
  app.querySelector<HTMLButtonElement>('#tocBtn')!.onclick = () => {
    lastFocused = document.activeElement as HTMLElement | null;
    toc.classList.add('open');
    app.querySelector<HTMLButtonElement>('#closeToc')!.focus();
  };
  app.querySelector<HTMLButtonElement>('#closeToc')!.onclick = closeToc;
  app.querySelector<HTMLElement>('#tocGrid')!.innerHTML = slides.map((slide, index) => slide.dataset.toc ? `<button data-jump="${index}">${slide.dataset.toc}</button>` : '').join('');

  app.onclick = (event) => {
    const target = event.target as HTMLElement;
    const combinedButton = target.closest<HTMLButtonElement>('[data-targets]');
    if (combinedButton) {
      const ids = (combinedButton.dataset.targets ?? '').split(/\s+/).filter(Boolean);
      const panels = ids.map((id) => app.querySelector<HTMLElement>(`#${CSS.escape(id)}`)).filter((panel): panel is HTMLElement => Boolean(panel));
      const willShow = panels.some((panel) => !panel.classList.contains('show'));
      panels.forEach((panel) => panel.classList.toggle('show', willShow));
      combinedButton.textContent = willShow ? '收起讲评' : '显示讲评';
      combinedButton.setAttribute('aria-expanded', String(willShow));
      void typeset(slides[current]!);
      return;
    }
    const revealButton = target.closest<HTMLButtonElement>('[data-target]');
    if (revealButton) {
      const panel = app.querySelector<HTMLElement>(`#${CSS.escape(revealButton.dataset.target ?? '')}`);
      panel?.classList.toggle('show');
      revealButton.setAttribute('aria-expanded', String(panel?.classList.contains('show')));
      void typeset(slides[current]!);
      return;
    }
    const jump = target.closest<HTMLButtonElement>('[data-jump]');
    if (jump) {
      closeToc();
      void show(Number(jump.dataset.jump), true);
      return;
    }
    const back = target.closest<HTMLButtonElement>('[data-back]');
    if (back) {
      void show(current - 1, true);
    }
  };
  app.querySelector<HTMLButtonElement>('#prev')!.onclick = () => void show(current - 1, true);
  app.querySelector<HTMLButtonElement>('#next')!.onclick = () => void show(current + 1, true);
  app.querySelector<HTMLButtonElement>('#quickBtn')!.onclick = () => {
    const index = slides.findIndex((slide) => slide.dataset.chapter === '要点速查');
    void show(index < 0 ? 0 : index, true);
  };
  app.querySelector<HTMLButtonElement>('#fullBtn')!.onclick = () => document.fullscreenElement ? void document.exitFullscreen() : void document.documentElement.requestFullscreen();

  document.onkeydown = (event) => {
    if (event.key === 'Escape') {
      closeToc();
      return;
    }
    if ((event.target as HTMLElement).closest('button, a, input, textarea, select')) return;
    if (event.key === 'ArrowRight' || event.key === 'PageDown' || event.key === ' ') {
      event.preventDefault();
      void show(current + 1, true);
    }
    if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
      event.preventDefault();
      void show(current - 1, true);
    }
  };

  let touchStart = 0;
  stage.addEventListener('touchstart', (event) => { touchStart = event.changedTouches[0]?.clientX ?? 0; }, { passive: true });
  stage.addEventListener('touchend', (event) => {
    const distance = (event.changedTouches[0]?.clientX ?? touchStart) - touchStart;
    if (Math.abs(distance) > 70) void show(current + (distance < 0 ? 1 : -1));
  }, { passive: true });

  await course.mount?.(app);
  await show(0);
}
